import React, { ReactNode } from 'react'
import { ExaminerRoles, ExaminerRole } from '../constants/examinerRole'

export const EXAMINER_ROLE_KEY = 'ukbc_examiner_role'

/**
 * Resolved role for the signed-in examiner (session). Null if not logged in as examiner.
 * After login, `examinerRole` from `/api/login` is stored by `apiLogin` as `ukbc_examiner_role`.
 */
export function getCurrentExaminerRole(): ExaminerRole | null {
    if (!sessionStorage.getItem('ukbc_examiner')) {
        return null
    }
    const raw = sessionStorage.getItem(EXAMINER_ROLE_KEY)
    if (raw && ExaminerRoles.is(raw)) {
        return raw
    }
    return null
}

/** True if the current examiner session has one of the allowed roles. */
export function examinerHasRole(allowed: ExaminerRole | readonly ExaminerRole[]): boolean {
    const cur = getCurrentExaminerRole()
    if (!cur) {
        return false
    }
    const list = (Array.isArray(allowed) ? allowed : [allowed]) as ExaminerRole[]
    return list.includes(cur)
}

interface RoleAccessProps {
    /** Show children only when the signed-in examiner has this role. */
    role?: ExaminerRole
    /** Show children when the examiner has any of these roles (preferred over `role` when set). */
    roles?: readonly ExaminerRole[]
    fallback?: ReactNode
    children: ReactNode
}

/**
 * Gate UI for the examiner app by staff role (`Examiner.role`).
 *
 * @example
 * <RoleAccess role="admin">…</RoleAccess>
 * @example
 * <RoleAccess roles={['superuser', 'examiner']}>…</RoleAccess>
 */
export default function RoleAccess({ role, roles, fallback = null, children }: RoleAccessProps) {
    let allowed = true
    if (roles !== undefined && roles.length > 0) {
        allowed = examinerHasRole(roles)
    } else if (role !== undefined) {
        allowed = examinerHasRole(role)
    }
    return allowed ? <>{children}</> : <>{fallback}</>
}
